import {useMemo, useState} from "react";

import {Card, CardContent} from "@/components/ui/card";
import {Loader} from "@/src/modules/core/components";
import useResidentHouseholdId from "@/src/modules/core/hooks/use-resident-household-id";
import useResidentMembership from "@/src/modules/core/hooks/use-resident-membership";
import {useStoredUserProperty} from "@/src/modules/core/store/user-property";
import useHousehold from "@/src/modules/households/hooks/use-household";
import {isAmenityKeyRestricted} from "@/src/modules/households/lib/household-amenity-restriction";
import {PermitDetailModal} from "@/src/modules/permanent-parking/components/permit-detail-modal";
import {usePermanentParkingPermits} from "@/src/modules/permanent-parking/hooks/use-permanent-parking-permits";
import {permanentParkingService} from "@/src/modules/permanent-parking/services";
import {cn} from "@/lib/utils";

import {
  RESIDENT_HOME_BLOCK_TITLE_CLASS,
  RESIDENT_HOME_CARD_CONTENT_CLASS,
  RESIDENT_HOME_CARD_SHELL_CLASS,
  RESIDENT_HOME_PILLAR_CARD_CONTENT_CLASS,
  RESIDENT_HOME_PILLAR_FILLER_CLASS,
  RESIDENT_HOME_PILLAR_FOOTER_PLACEHOLDER_CLASS,
  RESIDENT_HOME_PILLAR_HEADER_ACTION_SLOT_CLASS,
  RESIDENT_HOME_PILLAR_HEADER_ROW_CLASS,
  RESIDENT_HOME_PILLAR_LIST_SCROLL_CLASS,
  RESIDENT_HOME_PILLAR_LIST_SLOT_CLASS,
  RESIDENT_HOME_RECORD_PILLAR_PREVIEW_LIMIT,
  RESIDENT_HOME_TEXT_INSET_CLASS,
} from "../lib/resident-home-block-layout";
import {useHideResidentHomeTenantData} from "../hooks/use-hide-resident-home-tenant-data";
import {RESIDENT_HOME_PERMANENT_PERMITS_ICON} from "../lib/resident-home-teaser";
import {ResidentHomeCardPreviewFooter} from "./resident-home-card-preview-footer";
import {ResidentHomeIconTextBlock} from "./resident-home-icon-text-block";
import {ResidentHomeNoTenantData} from "./resident-home-no-tenant-data";
import {ResidentHomePermanentPermitsPreviewRow} from "./resident-home-permanent-permits-preview-row";
import {ResidentHomePermanentPermitsSheet} from "./resident-home-permanent-permits-sheet";
import {RESIDENT_PERMANENT_PARKING_RESTRICTION_MESSAGE} from "../lib/resident-amenity-restriction-copy";
import {ResidentAmenityRestrictionNotice} from "./resident-amenity-restriction-notice";

const PREVIEW_LIMIT = RESIDENT_HOME_RECORD_PILLAR_PREVIEW_LIMIT;

type ResidentHomePermanentPermitsCardProps = {
  className?: string;
};

export function ResidentHomePermanentPermitsCard({className}: ResidentHomePermanentPermitsCardProps) {
  const userProperty = useStoredUserProperty();
  const householdId = useResidentHouseholdId();
  const membership = useResidentMembership();
  const hideTenantData = useHideResidentHomeTenantData();
  const {household} = useHousehold(householdId, {enabled: !hideTenantData});
  const isParkingAmenityBlocked = isAmenityKeyRestricted(household, "permanent_parking");

  const [expandOpen, setExpandOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);

  const canShow = permanentParkingService.getIsShowEnabled();

  const filter = useMemo(
    () =>
      userProperty?.id && householdId
        ? {context_property_id: userProperty.id, household_id: householdId}
        : {},
    [householdId, userProperty?.id],
  );

  const {permits, totalRecords, isPending} = usePermanentParkingPermits({
    rows: PREVIEW_LIMIT,
    page: 1,
    filters: filter,
    sort: "-end_date",
    enabled: !hideTenantData && Boolean(userProperty?.id && householdId && canShow),
  });

  const showExpand = totalRecords > PREVIEW_LIMIT;

  return (
    <>
      <Card className={cn(RESIDENT_HOME_CARD_SHELL_CLASS, "flex h-full min-h-0 flex-col", className)}>
        <CardContent
          className={cn(
            RESIDENT_HOME_CARD_CONTENT_CLASS,
            RESIDENT_HOME_PILLAR_CARD_CONTENT_CLASS,
            "min-h-0 flex-1",
          )}
        >
          {!hideTenantData && isParkingAmenityBlocked ? (
            <ResidentAmenityRestrictionNotice message={RESIDENT_PERMANENT_PARKING_RESTRICTION_MESSAGE} />
          ) : null}

          <div className={RESIDENT_HOME_PILLAR_HEADER_ROW_CLASS}>
            <ResidentHomeIconTextBlock
              icon={RESIDENT_HOME_PERMANENT_PERMITS_ICON.icon}
              iconWrapperClassName={RESIDENT_HOME_PERMANENT_PERMITS_ICON.iconWrapperClassName}
              iconClassName={RESIDENT_HOME_PERMANENT_PERMITS_ICON.iconClassName}
              className="min-w-0 flex-1 items-start"
              title={<p className={RESIDENT_HOME_BLOCK_TITLE_CLASS}>Permanent permits</p>}
            />
            <span className={RESIDENT_HOME_PILLAR_HEADER_ACTION_SLOT_CLASS} aria-hidden>
              ·
            </span>
          </div>

          <div className={cn(RESIDENT_HOME_PILLAR_LIST_SLOT_CLASS, RESIDENT_HOME_TEXT_INSET_CLASS)}>
            {hideTenantData ? (
              <ResidentHomeNoTenantData />
            ) : !canShow ? (
              <p className="text-muted-foreground text-sm">
                You do not have permission to view permanent permits.
              </p>
            ) : !householdId ? (
              <p className="text-muted-foreground text-sm">
                Link a household to view permanent permits.
              </p>
            ) : isPending ? (
              <Loader className="min-h-24" />
            ) : permits.length === 0 ? (
              <p className="text-muted-foreground text-sm">No permanent permits yet.</p>
            ) : (
              <div className={RESIDENT_HOME_PILLAR_LIST_SCROLL_CLASS}>
                {permits.map((row) => (
                  <ResidentHomePermanentPermitsPreviewRow
                    key={row.id}
                    permit={row}
                    membership={membership}
                    amenityRestricted={isParkingAmenityBlocked}
                    onSelect={(id) => canShow && setDetailId(id)}
                  />
                ))}
              </div>
            )}
          </div>

          <div className={RESIDENT_HOME_PILLAR_FILLER_CLASS} aria-hidden />

          {!hideTenantData && canShow && householdId && !isPending && permits.length > 0 ? (
            <ResidentHomeCardPreviewFooter
              showingCount={permits.length}
              totalRecords={totalRecords}
              onShowMore={showExpand ? () => setExpandOpen(true) : undefined}
            />
          ) : (
            <div aria-hidden className={RESIDENT_HOME_PILLAR_FOOTER_PLACEHOLDER_CLASS} />
          )}
        </CardContent>
      </Card>

      <ResidentHomePermanentPermitsSheet
        open={expandOpen}
        onOpenChange={setExpandOpen}
        filter={filter}
        householdId={householdId}
        isParkingAmenityBlocked={isParkingAmenityBlocked}
        canShowDetail={canShow}
        onSelectDetail={(id) => setDetailId(id)}
      />

      <PermitDetailModal
        open={detailId != null}
        permitId={detailId}
        hideStatusActions
        onOpenChange={(open) => !open && setDetailId(null)}
      />
    </>
  );
}
